import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useSwipeable } from 'react-swipeable';
import { ProductTile } from '../types/productTiles';

interface ProductTileCardProps {
  products: ProductTile[];
  displayName: string;
  category: string;
  autoAdvance?: boolean;
  interval?: number;
  showDots?: boolean;
  onCategoryClick?: (category: string) => void;
  formatClickQuestion?: (category: string) => string;
  aspectRatio?: string;
  minHeight?: number;
  maxHeight?: number;
  swipeEnabled?: boolean;
}

export const ProductTileCard: React.FC<ProductTileCardProps> = ({
  products,
  displayName,
  category,
  autoAdvance = true,
  interval = 3000,
  showDots = true,
  onCategoryClick,
  formatClickQuestion,
  aspectRatio: _aspectRatio = '4:5', // Prefixed with _ to indicate intentionally unused
  minHeight: _minHeight = 300, // Not used with Grid layout
  maxHeight: _maxHeight = 600, // Not used with Grid layout
  swipeEnabled = true,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-advance carousel
  useEffect(() => {
    if (!autoAdvance || isHovered || products.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % products.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoAdvance, interval, isHovered, products.length]);

  // Navigation handlers
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % products.length);
  }, [products.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + products.length) % products.length);
  }, [products.length]);

  // Swipe handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: goToNext,
    onSwipedRight: goToPrevious,
    trackMouse: false,
    preventScrollOnSwipe: true,
  });

  // Handle category click
  const handleCategoryClick = () => {
    if (onCategoryClick) {
      // Use formatClickQuestion if provided, otherwise just use category
      const question = formatClickQuestion ? formatClickQuestion(category) : category;
      onCategoryClick(question);
    }
  };

  if (products.length === 0) return null;

  const currentProduct = products[currentIndex];

  return (
    <Box
      {...(swipeEnabled ? swipeHandlers : {})}
      onClick={handleCategoryClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          transform: 'translateY(-4px)',
        },
      }}
    >
      {/* Image Container with Aspect Ratio */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Product Image */}
        <Box
          component="img"
          src={currentProduct.image_url}
          alt={currentProduct.title}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          }}
        />

        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '50%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
            pointerEvents: 'none',
          }}
        />

        {/* Category Label */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            right: 16,
            zIndex: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#ffffff',
              fontWeight: 600,
              fontSize: { xs: '1.1rem', sm: '1rem', md: '0.845rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </Typography>
        </Box>

        {/* Carousel Dots */}
        {showDots && products.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              display: 'flex',
              gap: 0.5,
              zIndex: 2,
            }}
          >
            {products.map((_, index) => (
              <Box
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(index);
                }}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: index === currentIndex
                    ? '#ffffff'
                    : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#ffffff',
                    transform: 'scale(1.2)',
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
